import { useMemo } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import { useUsageLicenseDeniedCustomerQuery } from "@/v2-backoffice-client/usage/denied/usage.deniedCustomer.query"
import { LICENSE_DENIED_SECTION_TOOLTIP } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import {
  deriveLicenseDeniedWindow,
  formatLicenseDeniedFeature,
  type UsageLicenseDeniedFeatureRow,
} from "@/v2-backoffice-client/usage/denied/usage.denied.utils"

export type UsageLicenseDeniedCalloutProps = {
  customerId: number
  externalCustomerId: string
  className?: string
}

const FEATURE_PREVIEW_LIMIT = 3
const EMPTY_FEATURES: UsageLicenseDeniedFeatureRow[] = []

// The one-sentence version of "blocked by license" for the customer's
// "What they get" tab, where access is decided: while the operator reads
// what the customer's packages carry, this says what the people asked for
// and did not get — the features to add, with a way to the full picture on
// the Activity tab. Renders nothing when nobody has met a lock in 28 days.
function UsageLicenseDeniedCallout(props: UsageLicenseDeniedCalloutProps) {
  const { customerId, externalCustomerId, className } = props

  const { data } = useUsageLicenseDeniedCustomerQuery({
    variables: { customerId },
  })
  const features =
    data?.getUsageLicenseDeniedByCustomer.Features ?? EMPTY_FEATURES
  const members = data?.getUsageLicenseDeniedByCustomer.Members ?? []
  const summary = useMemo(() => deriveLicenseDeniedWindow(features), [features])
  const people28d = members.filter((member) => member.Attempts28d > 0).length
  const preview = features
    .filter((feature) => feature.Attempts28d > 0)
    .slice(0, FEATURE_PREVIEW_LIMIT)

  if (summary.attempts28d === 0) return null

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border border-amber-400/50 bg-amber-400/10 px-3 py-2.5 text-sm",
        className,
      )}
      role="status"
      title={LICENSE_DENIED_SECTION_TOOLTIP}
    >
      <Icon src="lock" size={16} className="mt-0.5 shrink-0 text-amber-500" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {people28d} {people28d === 1 ? "person" : "people"} tried{" "}
          {formatNumber(summary.attempts28d)} time
          {summary.attempts28d === 1 ? "" : "s"} to use {summary.features28d}{" "}
          locked feature
          {summary.features28d === 1 ? "" : "s"} in the last 28 days
          {summary.lastAt && (
            <>
              , last <RelativeDate date={summary.lastAt} />
            </>
          )}
          .
        </p>
        <p className="text-xs text-muted-foreground">
          {preview
            .map(
              (feature) =>
                `${formatLicenseDeniedFeature(feature.FeatureCode, feature.FeatureName)} (${formatNumber(feature.Attempts28d)})`,
            )
            .join(" · ")}
          {summary.features28d > preview.length &&
            ` · +${summary.features28d - preview.length} more`}{" "}
          — the packages that carry them are the upsell.{" "}
          <Link
            variant="muted"
            size="xs"
            iconSrc={null}
            className="underline underline-offset-2"
            to={buildCustomerViewLink(externalCustomerId, "sign-ins")}
          >
            Who asked, on Activity
          </Link>
        </p>
      </div>
    </div>
  )
}

export default UsageLicenseDeniedCallout
